import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableCell, TableRow, TableSortLabel,
    Box, LinearProgress,
    Snackbar, Alert, IconButton, Tooltip,
    TextField, MenuItem,
} from '@mui/material';
import { Receipt, Visibility, Download } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Loader } from '../../../shared/components/Loader';
import { tdsService } from '../../../shared/services/tdsService';
import { TDS } from '../../../shared/types/tds';

interface TDSFilters {
    client: string;
    dateFrom: string;
    dateTo: string;
}

const EMPTY_FILTERS: TDSFilters = { client: '', dateFrom: '', dateTo: '' };

export const TDSList: React.FC = () => {
    const navigate = useNavigate();
    const [tdsList, setTdsList] = useState<TDS[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>({ active: 'date', direction: 'desc' });
    const [grandTotal, setGrandTotal] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });
    const [pendingFilters, setPendingFilters] = useState<TDSFilters>(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState<TDSFilters>(EMPTY_FILTERS);

    const fetchTDS = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await tdsService.list({ page: 1, limit: 10000 });

            const outerData = res?.data;
            const items = outerData?.data || outerData?.items || (outerData as any)?.tds || [];
            setGrandTotal(outerData?.grandTotalAmount || 0);

            setTdsList(
                (Array.isArray(items) ? items : []).map((t: any) => ({
                    ...t,
                    _id: t._id ?? t.id,
                    clientIdStr: typeof t.clientId === 'object'
                        ? (t.clientId?._id || t.clientId?.id || '')
                        : (t.clientId || ''),
                    clientName:
                        typeof t.clientId === 'object'
                            ? (t.clientId?.displayName || t.clientId?.companyName || t.clientId?.ownerName)
                            : (t.clientCompanyName || t.clientName),
                }))
            );
        } catch {
            setSnackbar({ open: true, message: 'Error loading TDS records', severity: 'error' });
            setTdsList([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTDS();
    }, [fetchTDS]);

    const uniqueClients = React.useMemo(() => {
        const seen = new Set<string>();
        return tdsList.filter(t => {
            const id = (t as any).clientIdStr;
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
        }).map(t => ({ id: (t as any).clientIdStr as string, name: (t as any).clientName as string }));
    }, [tdsList]);

    const filteredList = React.useMemo(() => {
        let list = [...tdsList];

        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            list = list.filter(t =>
                t.invoiceNumber?.toLowerCase().includes(q) ||
                (t as any).clientName?.toLowerCase().includes(q) ||
                t.remark?.toLowerCase().includes(q)
            );
        }

        // Client filter by clientIdStr
        if (appliedFilters.client) {
            list = list.filter(t => (t as any).clientIdStr === appliedFilters.client);
        }

        // Date From filter
        if (appliedFilters.dateFrom) {
            const from = new Date(appliedFilters.dateFrom).getTime();
            list = list.filter(t => t.date && new Date(t.date).getTime() >= from);
        }

        // Date To filter
        if (appliedFilters.dateTo) {
            const to = new Date(appliedFilters.dateTo).setHours(23, 59, 59, 999);
            list = list.filter(t => t.date && new Date(t.date).getTime() <= to);
        }

        if (sortData?.active) {
            list.sort((a, b) => {
                let av: any;
                let bv: any;

                if (sortData.active === 'invoiceTotalAmount') {
                    av = typeof a.invoiceId === 'object' ? a.invoiceId?.totalAmount : 0;
                    bv = typeof b.invoiceId === 'object' ? b.invoiceId?.totalAmount : 0;
                } else if (sortData.active === 'creditedAmount') {
                    av = a.creditedAmount ?? 0;
                    bv = b.creditedAmount ?? 0;
                } else {
                    av = (a as any)[sortData.active] ?? '';
                    bv = (b as any)[sortData.active] ?? '';
                }

                if (typeof av === 'string' && typeof bv === 'string')
                    return sortData.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                if (av < bv) return sortData.direction === 'asc' ? -1 : 1;
                if (av > bv) return sortData.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return list;
    }, [tdsList, searchInput, sortData, appliedFilters]);

    const paginatedList = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredList.slice(start, start + pageSize);
    }, [filteredList, currentPage, pageSize]);

    const handleSort = (col: string) => {
        setSortData(prev =>
            prev?.active === col
                ? { active: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { active: col, direction: 'asc' }
        );
    };

    const handleApplyFilters = () => {
        setAppliedFilters(pendingFilters);
        setCurrentPage(0);
    };

    const handleResetFilters = () => {
        setPendingFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
        setCurrentPage(0);
    };

    const handleExport = () => {
        const headers = ['Invoice Number', 'Client', 'Invoice Total Amount', 'Credited Amount', 'Deducted TDS', 'Date'];
        const rows = filteredList.map(item => [
            item.invoiceNumber,
            (item as any).clientName || '',
            typeof item.invoiceId === 'object' ? item.invoiceId?.totalAmount : 0,
            item.creditedAmount ?? 0,
            item.amount,
            formatDate(item.date),
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tds-report.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const SortableHeader = ({ col, label }: { col: string; label: string }) => (
        <TableCell className="!font-semibold !text-gray-700">
            <TableSortLabel
                active={sortData?.active === col}
                direction={sortData?.active === col ? sortData.direction : 'asc'}
                onClick={() => handleSort(col)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const formatAmount = (amount: number) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
    };

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Receipt className="text-[#6366f1] w-8 h-8" />
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        TDS
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={(v) => { setSearchInput(v); setCurrentPage(0); }}
                        onSearch={() => setCurrentPage(0)}
                        placeholder="Search TDS..."
                        className="w-full sm:w-64"
                    />
                </div>
            </div>

            {/* Summary Bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 sm:px-10 py-2 border-b border-gray-100">
                <div className="flex gap-2">
                    <Tooltip title="Export TDS Report">
                        <AppButton
                            variant="primary"
                            onClick={handleExport}
                            sx={{ borderRadius: '8px', backgroundColor: '#0EA5E9', '&:hover': { backgroundColor: '#0284C7' }, px: 2, gap: 1 }}
                        >
                            <Download fontSize="small" />
                            Export TDS Report
                        </AppButton>
                    </Tooltip>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <AppButton
                        variant="primary"
                        disableRipple
                        sx={{
                            backgroundColor: '#22c55e',
                            '&:hover': { backgroundColor: '#22c55e' },
                            cursor: 'default',
                            px: 2,
                            py: 1,
                            borderRadius: '6px',
                            minWidth: 240,
                        }}
                    >
                        Total TDS Amount : {formatAmount(grandTotal)}
                    </AppButton>
                </div>
            </div>

            {/* Table */}
            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">

                {/* Filters Panel */}
                <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5 mb-6">
                    <div className="mb-4">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#6366f1]">Filters</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Client Dropdown */}
                        <TextField
                            select
                            label="Client"
                            value={pendingFilters.client}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, client: e.target.value }))}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="">All Clients</MenuItem>
                            {uniqueClients.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </TextField>

                        {/* Date From */}
                        <TextField
                            label="Date From"
                            type="date"
                            value={pendingFilters.dateFrom}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />

                        {/* Date To */}
                        <TextField
                            label="Date To"
                            type="date"
                            value={pendingFilters.dateTo}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                    <div className="flex gap-2 mt-4">
                        <AppButton
                            variant="primary"
                            onClick={handleApplyFilters}
                            sx={{ px: 3, py: 1, borderRadius: '6px' }}
                        >
                            Search
                        </AppButton>
                        <AppButton
                            variant="secondary"
                            onClick={handleResetFilters}
                            sx={{ px: 3, py: 1, borderRadius: '6px' }}
                        >
                            Reset
                        </AppButton>
                    </div>
                </div>

                <div className="flex flex-col w-full flex-1">
                    {isLoading && tdsList.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && tdsList.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <SortableHeader col="invoiceNumber" label="Invoice No" />
                                    <SortableHeader col="clientName" label="Client" />
                                    <SortableHeader col="invoiceTotalAmount" label="Invoice Total Amount" />
                                    <SortableHeader col="creditedAmount" label="Credited Amount" />
                                    <SortableHeader col="amount" label="Deducted TDS" />
                                    <SortableHeader col="date" label="Date" />
                                    <TableCell className="!font-semibold !text-gray-700">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredList.length === 0}
                            emptyColSpan={8}
                        >
                            {paginatedList.map((item, i) => (
                                <TableRow
                                    key={item._id}
                                    className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                >
                                    <TableCell className="!text-gray-500 !text-sm">
                                        {currentPage * pageSize + i + 1}
                                    </TableCell>
                                    <TableCell className="!text-gray-900 !font-medium">
                                        {item.invoiceNumber}
                                    </TableCell>
                                    <TableCell className="!text-gray-700">
                                        {(item as any).clientName || '—'}
                                    </TableCell>
                                    <TableCell className="!text-gray-700 !font-semibold">
                                        {formatAmount(typeof item.invoiceId === 'object' ? item.invoiceId?.totalAmount : 0)}
                                    </TableCell>
                                    <TableCell className="!text-gray-700 !font-semibold">
                                        {formatAmount(item.creditedAmount ?? 0)}
                                    </TableCell>
                                    <TableCell className="!text-gray-700 !font-semibold">
                                        {formatAmount(item.amount)}
                                    </TableCell>
                                    <TableCell className="!text-gray-600">
                                        {formatDate(item.date)}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="View Invoice">
                                            <IconButton
                                                onClick={() => {
                                                    const targetId = typeof item.invoiceId === 'object' ? item.invoiceId?.id : item.invoiceId;
                                                    navigate(`/invoices/view/${targetId}`, {
                                                        state: { backTo: '/tds' },
                                                    });
                                                }}
                                                size="small"
                                                className="text-[#0EA5E9] hover:bg-blue-50"
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredList.length}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={(_, p) => setCurrentPage(p - 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
                />
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity as any} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
